import { createSelector } from '@ngrx/store';
import { CooUploadFileCategory } from '@shared/services/hydration/ku4-draft.models';
import { selectFilesMetadata } from '@store/selectors/document-upload/document-upload.selectors';
import { selectLetterFilesMetadata } from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectTaxFilesMetadata } from '@store/selectors/tax-document-upload/tax-document-upload.selectors';

// KU4 constrains UploadedFile.fileType to this list, and co0-draft-library forwards the value
// without validating it, so anything else is omitted rather than risking a 400 on the PATCH.
const KU4_FILE_TYPES = new Set([
  'json',
  'csv',
  'txt',
  'pdf',
  'docx',
  'xlsx',
  'html',
  'png',
  'jpg',
  'jpeg',
]);

export interface DraftFileInfo {
  filename: string;
  fileExtension?: string;
  category: CooUploadFileCategory;
}

interface DraftFileSource {
  fileName: string;
  objectURL: string;
  s3BucketFileName?: string;
}

/**
 * Maps a slice's file metadata to the shape co0-draft-library expects.
 *
 * `filename` must be lowercase — the library filters on `!!f.filename`, so sending the NgRx
 * `fileName` silently dropped every file. It carries the unique S3 key rather than the display
 * name, because that key is what fetches the bytes back on resume.
 *
 * Only files that reached S3 are persisted, so everything recorded in a draft is restorable.
 */
export function mapFilesForDraft(
  files: DraftFileSource[],
  category: CooUploadFileCategory,
): DraftFileInfo[] {
  return files
    .filter((file) => !!file.s3BucketFileName)
    .map((file) => {
      const key = file.s3BucketFileName!;
      const dotIndex = key.lastIndexOf('.');
      const extension = dotIndex > 0 ? key.slice(dotIndex + 1).toLowerCase() : '';
      const info: DraftFileInfo = { filename: key, category };

      if (KU4_FILE_TYPES.has(extension)) {
        info.fileExtension = extension;
      }

      return info;
    });
}

export const selectDraftDocumentFiles = createSelector(
  selectFilesMetadata,
  selectLetterFilesMetadata,
  selectTaxFilesMetadata,
  (documentFiles, letterFiles, taxFiles) => ({
    documentFiles: mapFilesForDraft(documentFiles, 'DOCUMENT'),
    letterFiles: mapFilesForDraft(letterFiles, 'LETTER'),
    // The library reads three arrays and concatenates them, so the KU4 payload is identical
    // whichever is used. caseInformationFiles is the third declared slot; the L8/ITW files go
    // here despite the field name, keeping one array per NgRx slice.
    caseInformationFiles: mapFilesForDraft(taxFiles, 'TAX'),
  }),
);
